import React, { useState, useEffect, useMemo } from 'react'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
} from 'recharts'
import type { NameType } from 'recharts/types/component/DefaultTooltipContent'
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Download,
  ChefHat,
  DollarSign,
  BarChart3,
  PieChart as PieChartIcon,
  Award,
  Target,
  ShoppingBag,
  Flame,
} from 'lucide-react'
import { useCurrency } from '@/hooks/useCurrency'
import { ApiService } from '@/services/api'
import { createDateRange, parseDate, formatDateForAPI } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import type { DateRange } from '@/types'

interface ProfitReportItem {
  name: string
  category: string
  recipeCost: number
  sellingPrice: number
  quantitySold: number
  totalSales: number
  totalCost: number
  foodCostPercent: number
  totalProfit: number
}

interface ProfitReportData {
  summary: {
    foodCostPercent: number
    totalCost: number
    totalSales: number
    totalProfit: number
  }
  items: ProfitReportItem[]
  meta: {
    itemsTracked: number
    totalQuantity: number
  }
}

const CHART_COLORS = {
  profit: '#10b981',
  cost: '#f59e0b',
  sales: '#3b82f6',
  danger: '#ef4444',
  purple: '#8b5cf6',
  teal: '#14b8a6',
}

const PIE_COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#14b8a6', '#f43f5e', '#f97316', '#84cc16']

function getFoodCostColor(pct: number): string {
  if (pct >= 60) return '#ef4444'
  if (pct >= 35) return '#f59e0b'
  return '#10b981'
}

function getFoodCostLabel(pct: number): string {
  if (pct >= 60) return 'High'
  if (pct >= 35) return 'Moderate'
  return 'Healthy'
}

function getFoodCostBadgeClass(pct: number): string {
  if (pct >= 60) return 'bg-red-100 text-red-700 border-red-200'
  if (pct >= 35) return 'bg-yellow-100 text-yellow-700 border-yellow-200'
  return 'bg-green-100 text-green-700 border-green-200'
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const { formatCurrency } = useCurrency()
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 text-sm">
      <p className="font-semibold mb-2 text-gray-800 dark:text-gray-100">{label}</p>
      {payload.map((entry: any) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-600 dark:text-gray-300">{entry.name}:</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {typeof entry.value === 'number' && entry.value > 100
              ? formatCurrency(entry.value)
              : `${entry.value}${entry.dataKey === 'foodCostPercent' ? '%' : ''}`}
          </span>
        </div>
      ))}
    </div>
  )
}

const PieTooltip = ({ active, payload }: any) => {
  const { formatCurrency } = useCurrency()
  if (!active || !payload?.length) return null
  const { name, value, payload: p } = payload[0]
  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 text-sm">
      <p className="font-semibold text-gray-800 dark:text-gray-100">{name}</p>
      <p className="text-gray-600 dark:text-gray-300">{formatCurrency(value)}</p>
      {p?.percent && <p className="text-gray-500">{(p.percent * 100).toFixed(1)}% of total</p>}
    </div>
  )
}

export default function ProfitReportScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const { currentRestaurant } = useRestaurant()
  const [reportData, setReportData] = useState<ProfitReportData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [activeTab, setActiveTab] = useState<'overview' | 'items' | 'analysis'>('overview')

  const loadReport = async () => {
    if (!currentRestaurant?._id) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getProfitReport(currentRestaurant._id, dateRange)
      if (response.success) {
        setReportData(response.data)
      } else {
        setError(response.message || 'Failed to load profit report')
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message || 'Failed to load profit report'
        : 'Failed to load profit report'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (currentRestaurant?._id) loadReport()
  }, [currentRestaurant?._id])

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    setDateRange(prev => {
      const newDate = parseDate(value)
      return field === 'from'
        ? createDateRange(newDate, prev.to)
        : createDateRange(prev.from, newDate)
    })
  }

  const exportToCSV = () => {
    if (!reportData) return
    const headers = ['Item', 'Category', 'Cost Of Production', 'Selling Price', 'Qty Sold', 'Food Cost %', 'Total Sales', 'Total Cost', 'Total Profit']
    const rows = reportData.items.map(item => [
      item.name, item.category,
      item.recipeCost.toFixed(2), item.sellingPrice.toFixed(2),
      item.quantitySold, item.foodCostPercent.toFixed(1) + '%',
      item.totalSales.toFixed(2), item.totalCost.toFixed(2), item.totalProfit.toFixed(2)
    ])
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `profit-report-${formatDateForAPI(dateRange.from)}-to-${formatDateForAPI(dateRange.to)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const derived = useMemo(() => {
    if (!reportData) return null
    const { summary, items } = reportData

    const top5Profit = [...items]
      .sort((a, b) => b.totalProfit - a.totalProfit)
      .slice(0, 5)

    const top5Sales = [...items]
      .sort((a, b) => b.totalSales - a.totalSales)
      .slice(0, 5)

    const foodCostDistribution = [
      { name: 'Healthy (<35%)', value: items.filter(i => i.foodCostPercent < 35).length, color: '#10b981' },
      { name: 'Moderate (35-60%)', value: items.filter(i => i.foodCostPercent >= 35 && i.foodCostPercent < 60).length, color: '#f59e0b' },
      { name: 'High (≥60%)', value: items.filter(i => i.foodCostPercent >= 60).length, color: '#ef4444' },
    ].filter(d => d.value > 0)

    const revenueBars = [
      { name: 'Sales', Sales: summary.totalSales },
      { name: 'Cost', Cost: summary.totalCost },
      { name: 'Profit', Profit: summary.totalProfit },
    ]

    const breakdownPie = [
      { name: 'Total Cost', value: summary.totalCost },
      { name: 'Net Profit', value: Math.max(summary.totalProfit, 0) },
    ]

    const categorySummary: Record<string, { sales: number; cost: number; profit: number; qty: number }> = {}
    items.forEach(item => {
      if (!categorySummary[item.category]) {
        categorySummary[item.category] = { sales: 0, cost: 0, profit: 0, qty: 0 }
      }
      categorySummary[item.category].sales += item.totalSales
      categorySummary[item.category].cost += item.totalCost
      categorySummary[item.category].profit += item.totalProfit
      categorySummary[item.category].qty += Number(item.quantitySold) || 0
    })

    const categoryData = Object.entries(categorySummary)
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.sales - a.sales)

    const radialData = [
      {
        name: 'Food Cost',
        value: Math.min(summary.foodCostPercent, 100),
        fill: getFoodCostColor(summary.foodCostPercent),
      },
    ]

    const bestItem = items.reduce((best, item) => item.totalProfit > (best?.totalProfit ?? -Infinity) ? item : best, items[0])
    const worstItem = items.reduce((worst, item) => item.totalProfit < (worst?.totalProfit ?? Infinity) ? item : worst, items[0])
    const highestMarginItem = items.reduce((best, item) => {
      const margin = item.sellingPrice > 0 ? ((item.sellingPrice - item.recipeCost) / item.sellingPrice) * 100 : 0
      const bestMargin = best ? ((best.sellingPrice - best.recipeCost) / best.sellingPrice) * 100 : -Infinity
      return margin > bestMargin ? item : best
    }, items[0])

    return { top5Profit, top5Sales, foodCostDistribution, revenueBars, breakdownPie, categoryData, radialData, bestItem, worstItem, highestMarginItem }
  }, [reportData])

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-green-100 border-t-green-500 animate-spin" />
          <ChefHat className="absolute inset-0 m-auto h-6 w-6 text-green-600" />
        </div>
        <p className="text-muted-foreground text-sm">Loading profit report...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="flex items-center gap-2">
          {error}
          <Button variant="outline" size="sm" className="py-[10px]" onClick={loadReport}>
            <RefreshCw className="h-4 w-4 mr-1" /> Retry
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  if (!reportData || !derived) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>No data available for the selected period.</AlertDescription>
      </Alert>
    )
  }

  const { summary, items, meta } = reportData
  const { top5Profit, top5Sales, foodCostDistribution, revenueBars, breakdownPie, categoryData, radialData, bestItem, worstItem, highestMarginItem } = derived
  const profitPct = summary.totalSales > 0 ? (summary.totalProfit / summary.totalSales) * 100 : 0
  const costPct = summary.totalSales > 0 ? (summary.totalCost / summary.totalSales) * 100 : 0

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex-shrink-0">
            <ChefHat className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold truncate">Profit Report</h1>
            <p className="text-xs text-muted-foreground hidden sm:block">Food cost & profitability analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="icon" onClick={loadReport} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={exportToCSV} title="Export CSV">
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Date filter — desktop */}
      <div className="hidden md:flex gap-3 items-center">
        <div className="flex-1">
          <DateRangeModal
            dateRange={dateRange}
            onDateChange={handleDateChange}
            onApply={loadReport}
            title="Profit Report Period"
            description="Select the date range for the profit report"
          />
        </div>
        <Button onClick={loadReport} disabled={isLoading} className="flex items-center gap-2">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
          Apply
        </Button>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        {/* Total Sales */}
        <Card className="relative overflow-hidden border-0 shadow-md bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-blue-100 text-xs font-medium uppercase tracking-wide">Total Sales</p>
              <DollarSign className="h-4 w-4 text-blue-200" />
            </div>
            <p className="text-2xl font-bold">{formatCurrency(summary.totalSales)}</p>
            <p className="text-blue-200 text-xs mt-1">{meta.totalQuantity} items sold</p>
          </CardContent>
          <div className="absolute -right-4 -bottom-4 w-20 h-20 rounded-full bg-blue-400/20" />
        </Card>

        {/* Total Cost */}
        <Card className="relative overflow-hidden border-0 shadow-md bg-gradient-to-br from-amber-500 to-orange-500 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-amber-100 text-xs font-medium uppercase tracking-wide">Total Cost</p>
              <ShoppingBag className="h-4 w-4 text-amber-200" />
            </div>
            <p className="text-2xl font-bold">{formatCurrency(summary.totalCost)}</p>
            <p className="text-amber-200 text-xs mt-1">{costPct.toFixed(1)}% of sales</p>
          </CardContent>
          <div className="absolute -right-4 -bottom-4 w-20 h-20 rounded-full bg-amber-400/20" />
        </Card>

        {/* Total Profit */}
        <Card className={`relative overflow-hidden border-0 shadow-md text-white ${summary.totalProfit >= 0 ? 'bg-gradient-to-br from-emerald-500 to-green-600' : 'bg-gradient-to-br from-red-500 to-rose-600'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-green-100 text-xs font-medium uppercase tracking-wide">Net Profit</p>
              {summary.totalProfit >= 0
                ? <TrendingUp className="h-4 w-4 text-green-200" />
                : <TrendingDown className="h-4 w-4 text-red-200" />}
            </div>
            <p className="text-2xl font-bold">{formatCurrency(summary.totalProfit)}</p>
            <p className="text-green-200 text-xs mt-1">{profitPct.toFixed(1)}% margin</p>
          </CardContent>
          <div className="absolute -right-4 -bottom-4 w-20 h-20 rounded-full bg-green-400/20" />
        </Card>

        {/* Food Cost % */}
        <Card className={`relative overflow-hidden border-0 shadow-md text-white ${summary.foodCostPercent >= 60 ? 'bg-gradient-to-br from-red-500 to-rose-600' : summary.foodCostPercent >= 35 ? 'bg-gradient-to-br from-yellow-500 to-amber-600' : 'bg-gradient-to-br from-teal-500 to-cyan-600'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-teal-100 text-xs font-medium uppercase tracking-wide">Food Cost %</p>
              <Flame className="h-4 w-4 text-teal-200" />
            </div>
            <p className="text-2xl font-bold">{summary.foodCostPercent.toFixed(1)}%</p>
            <p className="text-teal-200 text-xs mt-1">{getFoodCostLabel(summary.foodCostPercent)} range</p>
          </CardContent>
          <div className="absolute -right-4 -bottom-4 w-20 h-20 rounded-full bg-teal-400/20" />
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-muted/50 rounded-xl w-full sm:w-auto">
        {(['overview', 'items', 'analysis'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
              activeTab === tab
                ? 'bg-white dark:bg-gray-800 shadow text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'overview' ? 'Overview' : tab === 'items' ? 'Items' : 'Analysis'}
          </button>
        ))}
      </div>

      {/* ─── OVERVIEW TAB ─── */}
      {activeTab === 'overview' && (
        <div className="space-y-4 sm:space-y-6">
          {/* Items Table */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">All Items</CardTitle>
              <CardDescription className="text-xs">{meta.itemsTracked} items tracked · {meta.totalQuantity} total sold</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {/* Desktop table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">#</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Item</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Cost</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Price</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Sold</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Total Sales</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Total Cost</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Profit</th>
                      <th className="text-center px-4 py-3 font-medium text-muted-foreground">Food Cost %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-12 text-muted-foreground">No items found for the selected period.</td>
                      </tr>
                    ) : (
                      items.map((item, idx) => (
                        <tr key={item.name} className="border-b hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 text-muted-foreground">{idx + 1}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{item.name}</div>
                            <div className="text-xs text-muted-foreground">{item.category}</div>
                          </td>
                          <td className="px-4 py-3 text-right">{formatCurrency(item.recipeCost)}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(item.sellingPrice)}</td>
                          <td className="px-4 py-3 text-right">{Number(item.quantitySold) || 0}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(item.totalSales)}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(item.totalCost)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${item.totalProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {formatCurrency(item.totalProfit)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getFoodCostBadgeClass(item.foodCostPercent)}`}>
                              {item.foodCostPercent.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="lg:hidden divide-y">
                {items.map((item, idx) => (
                  <div key={item.name} className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground flex-shrink-0">{idx + 1}</span>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{item.name}</div>
                          <div className="text-xs text-muted-foreground">{item.category}</div>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${getFoodCostBadgeClass(item.foodCostPercent)}`}>
                        {item.foodCostPercent.toFixed(1)}%
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
                      <div className="bg-muted/30 rounded-lg p-2">
                        <div className="font-semibold">{formatCurrency(item.recipeCost)}</div>
                        <div className="text-muted-foreground">COP</div>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-2">
                        <div className="font-semibold">{formatCurrency(item.sellingPrice)}</div>
                        <div className="text-muted-foreground">Price</div>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-2">
                        <div className="font-semibold">{Number(item.quantitySold) || 0}</div>
                        <div className="text-muted-foreground">Sold</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div>
                        <div className="font-semibold text-blue-600">{formatCurrency(item.totalSales)}</div>
                        <div className="text-muted-foreground">Sales</div>
                      </div>
                      <div>
                        <div className="font-semibold text-amber-600">{formatCurrency(item.totalCost)}</div>
                        <div className="text-muted-foreground">Cost</div>
                      </div>
                      <div>
                        <div className={`font-semibold ${item.totalProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatCurrency(item.totalProfit)}</div>
                        <div className="text-muted-foreground">Profit</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Revenue Breakdown Bar Chart */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-4 w-4 text-blue-500" />
                Revenue Breakdown
              </CardTitle>
              <CardDescription className="text-xs">Total sales vs cost vs profit comparison</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={revenueBars} margin={{ top: 4, right: 16, left: 0, bottom: 4 }} barCategoryGap="35%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${currencySymbol}${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="Sales" fill={CHART_COLORS.sales} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Cost" fill={CHART_COLORS.cost} radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Profit" fill={CHART_COLORS.profit} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Two charts side by side on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Cost vs Profit Donut */}
            <Card className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <PieChartIcon className="h-4 w-4 text-purple-500" />
                  Cost vs Profit
                </CardTitle>
                <CardDescription className="text-xs">Proportion of costs vs net profit</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={breakdownPie}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      <Cell fill={CHART_COLORS.cost} />
                      <Cell fill={CHART_COLORS.profit} />
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                    <Legend
                      formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-6 mt-2 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Cost</p>
                    <p className="font-bold text-amber-600">{costPct.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Profit</p>
                    <p className="font-bold text-emerald-600">{profitPct.toFixed(1)}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Food Cost Gauge */}
            <Card className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target className="h-4 w-4 text-teal-500" />
                  Food Cost Health
                </CardTitle>
                <CardDescription className="text-xs">Overall food cost percentage gauge</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={160}>
                  <RadialBarChart
                    cx="50%"
                    cy="80%"
                    innerRadius="60%"
                    outerRadius="100%"
                    startAngle={180}
                    endAngle={0}
                    data={radialData}
                  >
                    <RadialBar dataKey="value" background={{ fill: '#e5e7eb' }} cornerRadius={8} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="text-center -mt-4">
                  <p className="text-3xl font-bold" style={{ color: getFoodCostColor(summary.foodCostPercent) }}>
                    {summary.foodCostPercent.toFixed(1)}%
                  </p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border mt-1 ${getFoodCostBadgeClass(summary.foodCostPercent)}`}>
                    {getFoodCostLabel(summary.foodCostPercent)} Food Cost
                  </span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-3 px-2">
                  <span className="text-green-600 font-medium">0%</span>
                  <span className="text-yellow-600 font-medium">35%</span>
                  <span className="text-red-600 font-medium">60%+</span>
                </div>
                <div className="w-full h-2 rounded-full mt-1 bg-gradient-to-r from-green-400 via-yellow-400 to-red-500" />
              </CardContent>
            </Card>
          </div>

          {/* Category Performance */}
          {categoryData.length > 0 && (
            <Card className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="h-4 w-4 text-indigo-500" />
                  Category Performance
                </CardTitle>
                <CardDescription className="text-xs">Sales & profit by menu category</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={Math.max(180, categoryData.length * 45)}>
                  <BarChart
                    data={categoryData}
                    layout="vertical"
                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                    barCategoryGap="30%"
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${currencySymbol}${(v / 1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend formatter={(v) => <span className="text-xs">{v}</span>} />
                    <Bar dataKey="sales" name="Sales" fill={CHART_COLORS.sales} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="profit" name="Profit" fill={CHART_COLORS.profit} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Insight Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {bestItem && (
              <Card className="border-green-200 bg-green-50/60 dark:bg-green-950/20 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="h-4 w-4 text-green-600" />
                    <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Top Earner</span>
                  </div>
                  <p className="font-bold text-green-900 dark:text-green-200 text-sm">{bestItem.name}</p>
                  <p className="text-green-600 text-xs mt-1">{formatCurrency(bestItem.totalProfit)} profit</p>
                  <p className="text-green-500 text-xs">{(Number(bestItem.quantitySold) || 0)} units sold</p>
                </CardContent>
              </Card>
            )}
            {highestMarginItem && (
              <Card className="border-blue-200 bg-blue-50/60 dark:bg-blue-950/20 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-blue-600" />
                    <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Best Margin</span>
                  </div>
                  <p className="font-bold text-blue-900 dark:text-blue-200 text-sm">{highestMarginItem.name}</p>
                  <p className="text-blue-600 text-xs mt-1">
                    {highestMarginItem.sellingPrice > 0
                      ? (((highestMarginItem.sellingPrice - highestMarginItem.recipeCost) / highestMarginItem.sellingPrice) * 100).toFixed(1)
                      : '0'}% margin
                  </p>
                  <p className="text-blue-500 text-xs">{formatCurrency(highestMarginItem.sellingPrice)} price</p>
                </CardContent>
              </Card>
            )}
            {worstItem && (
              <Card className={`shadow-sm ${worstItem.totalProfit < 0 ? 'border-red-200 bg-red-50/60 dark:bg-red-950/20' : 'border-orange-200 bg-orange-50/60 dark:bg-orange-950/20'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className={`h-4 w-4 ${worstItem.totalProfit < 0 ? 'text-red-600' : 'text-orange-500'}`} />
                    <span className={`text-xs font-semibold uppercase tracking-wide ${worstItem.totalProfit < 0 ? 'text-red-700' : 'text-orange-600'}`}>Needs Attention</span>
                  </div>
                  <p className={`font-bold text-sm ${worstItem.totalProfit < 0 ? 'text-red-900 dark:text-red-200' : 'text-orange-900 dark:text-orange-200'}`}>{worstItem.name}</p>
                  <p className={`text-xs mt-1 ${worstItem.totalProfit < 0 ? 'text-red-600' : 'text-orange-600'}`}>{formatCurrency(worstItem.totalProfit)} profit</p>
                  <p className={`text-xs ${worstItem.totalProfit < 0 ? 'text-red-500' : 'text-orange-500'}`}>{worstItem.foodCostPercent.toFixed(1)}% food cost</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ─── ITEMS TAB ─── */}
      {activeTab === 'items' && (
        <div className="space-y-4">
          {/* Top 5 by Profit */}
          {top5Profit.length > 0 && (
            <Card className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  Top Items by Profit
                </CardTitle>
                <CardDescription className="text-xs">Highest earning items this period</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={Math.max(180, top5Profit.length * 50)}>
                  <BarChart
                    data={top5Profit.map(i => ({ name: i.name.length > 16 ? i.name.slice(0, 16) + '…' : i.name, Profit: i.totalProfit, Sales: i.totalSales }))}
                    layout="vertical"
                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                    barCategoryGap="25%"
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${currencySymbol}${(v / 1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend formatter={(v) => <span className="text-xs">{v}</span>} />
                    <Bar dataKey="Sales" fill={CHART_COLORS.sales} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="Profit" fill={CHART_COLORS.profit} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Food Cost % Distribution Pie */}
          {foodCostDistribution.length > 0 && (
            <Card className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <PieChartIcon className="h-4 w-4 text-amber-500" />
                  Food Cost Distribution
                </CardTitle>
                <CardDescription className="text-xs">Number of items in each food cost range</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row items-center gap-4">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={foodCostDistribution} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${value}`}>
                      {foodCostDistribution.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any, name: NameType | undefined) => [`${v} items`, String(name ?? '')] as [string, string]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 flex-shrink-0">
                  {foodCostDistribution.map(d => (
                    <div key={d.name} className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-sm text-muted-foreground">{d.name}</span>
                      <span className="font-bold text-sm ml-auto pl-4">{d.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Full Item List */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">All Items</CardTitle>
              <CardDescription className="text-xs">{meta.itemsTracked} items tracked · {meta.totalQuantity} total sold</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {/* Desktop table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">#</th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Item</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Cost</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Price</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Sold</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Total Sales</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Total Cost</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Profit</th>
                      <th className="text-center px-4 py-3 font-medium text-muted-foreground">Food Cost %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-12 text-muted-foreground">No items found for the selected period.</td>
                      </tr>
                    ) : (
                      items.map((item, idx) => (
                        <tr key={item.name} className="border-b hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 text-muted-foreground">{idx + 1}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{item.name}</div>
                            <div className="text-xs text-muted-foreground">{item.category}</div>
                          </td>
                          <td className="px-4 py-3 text-right">{formatCurrency(item.recipeCost)}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(item.sellingPrice)}</td>
                          <td className="px-4 py-3 text-right">{Number(item.quantitySold) || 0}</td>
                          <td className="px-4 py-3 text-right font-medium">{formatCurrency(item.totalSales)}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(item.totalCost)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${item.totalProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {formatCurrency(item.totalProfit)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getFoodCostBadgeClass(item.foodCostPercent)}`}>
                              {item.foodCostPercent.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="lg:hidden divide-y">
                {items.map((item, idx) => (
                  <div key={item.name} className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground flex-shrink-0">{idx + 1}</span>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{item.name}</div>
                          <div className="text-xs text-muted-foreground">{item.category}</div>
                        </div>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${getFoodCostBadgeClass(item.foodCostPercent)}`}>
                        {item.foodCostPercent.toFixed(1)}%
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs mb-3">
                      <div className="bg-muted/30 rounded-lg p-2">
                        <div className="font-semibold">{formatCurrency(item.recipeCost)}</div>
                        <div className="text-muted-foreground">COP</div>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-2">
                        <div className="font-semibold">{formatCurrency(item.sellingPrice)}</div>
                        <div className="text-muted-foreground">Price</div>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-2">
                        <div className="font-semibold">{Number(item.quantitySold) || 0}</div>
                        <div className="text-muted-foreground">Sold</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div>
                        <div className="font-semibold text-blue-600">{formatCurrency(item.totalSales)}</div>
                        <div className="text-muted-foreground">Sales</div>
                      </div>
                      <div>
                        <div className="font-semibold text-amber-600">{formatCurrency(item.totalCost)}</div>
                        <div className="text-muted-foreground">Cost</div>
                      </div>
                      <div>
                        <div className={`font-semibold ${item.totalProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatCurrency(item.totalProfit)}</div>
                        <div className="text-muted-foreground">Profit</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── ANALYSIS TAB ─── */}
      {activeTab === 'analysis' && (
        <div className="space-y-4">
          {/* Food Cost % per item bar chart */}
          {items.length > 0 && (
            <Card className="shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Flame className="h-4 w-4 text-red-500" />
                  Food Cost % by Item
                </CardTitle>
                <CardDescription className="text-xs">Identify high-cost items that need pricing review</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={Math.max(200, items.length * 36)}>
                  <BarChart
                    data={[...items]
                      .sort((a, b) => b.foodCostPercent - a.foodCostPercent)
                      .slice(0, 12)
                      .map(i => ({
                        name: i.name.length > 18 ? i.name.slice(0, 18) + '…' : i.name,
                        foodCostPercent: parseFloat(i.foodCostPercent.toFixed(1)),
                        fill: getFoodCostColor(i.foodCostPercent),
                      }))}
                    layout="vertical"
                    margin={{ top: 4, right: 40, left: 8, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={110} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="foodCostPercent" name="Food Cost %" radius={[0, 4, 4, 0]}>
                      {[...items]
                        .sort((a, b) => b.foodCostPercent - a.foodCostPercent)
                        .slice(0, 12)
                        .map((item, i) => (
                          <Cell key={i} fill={getFoodCostColor(item.foodCostPercent)} />
                        ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center justify-center gap-6 mt-3 text-xs">
                  <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500" /> Healthy &lt;35%</div>
                  <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-yellow-500" /> Moderate 35–60%</div>
                  <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500" /> High ≥60%</div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Profit margin progress bars */}
          <Card className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Target className="h-4 w-4 text-indigo-500" />
                Financial Overview
              </CardTitle>
              <CardDescription className="text-xs">Revenue allocation breakdown</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-medium text-blue-700">Total Sales</span>
                  <span className="font-bold text-blue-700">{formatCurrency(summary.totalSales)}</span>
                </div>
                <Progress value={100} className="h-3 [&>div]:bg-blue-500" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-medium text-amber-700">Food Cost ({costPct.toFixed(1)}%)</span>
                  <span className="font-bold text-amber-700">{formatCurrency(summary.totalCost)}</span>
                </div>
                <Progress value={costPct} className="h-3 [&>div]:bg-amber-500" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className={`font-medium ${summary.totalProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    Net Profit ({Math.abs(profitPct).toFixed(1)}%)
                  </span>
                  <span className={`font-bold ${summary.totalProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                    {formatCurrency(summary.totalProfit)}
                  </span>
                </div>
                <Progress
                  value={Math.abs(profitPct)}
                  className={`h-3 ${summary.totalProfit >= 0 ? '[&>div]:bg-emerald-500' : '[&>div]:bg-red-500'}`}
                />
              </div>
            </CardContent>
          </Card>

          {/* Summary stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Items Tracked', value: meta.itemsTracked, color: 'text-blue-600' },
              { label: 'Total Sold', value: meta.totalQuantity, color: 'text-amber-600' },
              { label: 'Avg. Food Cost %', value: `${summary.foodCostPercent.toFixed(1)}%`, color: getFoodCostColor(summary.foodCostPercent) === '#10b981' ? 'text-emerald-600' : getFoodCostColor(summary.foodCostPercent) === '#f59e0b' ? 'text-amber-600' : 'text-red-600' },
              { label: 'Profit Margin', value: `${profitPct.toFixed(1)}%`, color: profitPct >= 0 ? 'text-emerald-600' : 'text-red-600' },
            ].map(stat => (
              <Card key={stat.label} className="shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Mobile floating filter */}
      <MobileFloatingFilter
        dateRange={dateRange}
        onDateChange={handleDateChange}
        onApply={loadReport}
        title="Profit Report Date Range"
        description="Select the date range for the profit report"
      />
    </div>
  )
}
